import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Device} from '@/model/network/device/Device';

import {INetworkDeviceService} from '@/services/network/INetworkDeviceService';
import {DeviceType} from '@/model/network/device/DeviceType';
import {DeviceEthernet} from '@/model/network/device/DeviceEthernet';

@Module({name: 'NetworkDevicesStore', namespaced: true})
export default class NetworkDevicesStore extends VuexModule {
  private devices: Device[] | null = null;

  @Inject(Symbol.for('INetworkDeviceService'))
  private deviceService!: INetworkDeviceService;

  @Action({commit: 'setDevices', rawError: true})
  public async fetchDevices(fetchDetails: boolean = false) {
    const devices = await this.deviceService.readAll();

    if (fetchDetails) {
      for (const device of devices) {
        Object.assign(device, await this.deviceService.read(device.Interface));
      }
    }

    return devices;
  }

  @Mutation
  public setDevices(devices: Device[]) {
    this.devices = devices.sort((a, b) => a.Id.localeCompare(b.Id));
  }

  public get Devices(): Device[] | null {
    return this.devices;
  }

  public get EthernetDevices(): DeviceEthernet[] | null {
    return this.devices?.filter((d) => d.Type === DeviceType.Ethernet) as DeviceEthernet[] ?? null;
  }

  @Action({rawError: true})
  public async fetchDevicesDetails() {

    if (!this.devices) {
      await this.fetchDevices();
    }

    if (!this.devices) {
      throw new Error('Device list still null after fetch');
    }

    for (const device of this.devices) {
      await this.fetchDevice(device.Interface);
    }
  }

  @Action({commit: 'setDevice', rawError: true})
  public async fetchDevice(interfaceName: string): Promise<Device> {
    return await this.deviceService.read(interfaceName);
  }

  @Mutation
  public setDevice(device: Device) {

    if (!this.devices) {
      this.devices = [];
    }

    for (const d of this.devices) {
      if (d.Interface === device.Interface) {
        Object.assign(d, device);
        return;
      }
    }

    this.devices.push(device);

    this.devices.sort((a, b) => a.Id.localeCompare(b.Id));
  }

  public getDevice(id: string): Device | null {
    return this.devices?.find((d) => d.Id === id) ?? null;
  }
}
